import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, Share } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "jobvinum_pwa_install_dismissed_at";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

/** Slim top banner encouraging PWA installation. Uses the native
 * `beforeinstallprompt` event on Chrome/Edge/Android; shows manual
 * "Add to Home Screen" instructions on iOS Safari (no native API there). */
export default function InstallPWABanner() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), 2500);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    const handleInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener("appinstalled", handleInstalled);

    let iosTimer;
    if (isIos()) {
      iosTimer = setTimeout(() => setVisible(true), 2500);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    setShowIosSteps(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") dismiss();
      else setVisible(false);
      setDeferredPrompt(null);
    } else if (isIos()) {
      setShowIosSteps(true);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="w-full bg-[#2B2B2B] text-white animate-in slide-in-from-top duration-500"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
        <img src="/icon-192.png" alt="" className="h-9 w-9 rounded-lg shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{t("pwa.installTitle")}</p>
          {!showIosSteps && <p className="text-xs text-white/60 truncate">{t("pwa.installDescription")}</p>}
          {showIosSteps && (
            <p className="text-xs text-[#dde165] flex items-center gap-1">
              <Share className="h-3.5 w-3.5 shrink-0" /> {t("pwa.iosInstructions")}
            </p>
          )}
        </div>
        {!showIosSteps && (
          <Button
            size="sm"
            onClick={handleInstall}
            className="rounded-lg bg-[#4ba3b7] hover:bg-[#3D8A9A] gap-1.5 shrink-0"
            data-testid="pwa-install-button"
          >
            <Download className="h-3.5 w-3.5" /> {t("pwa.installButton")}
          </Button>
        )}
        <button
          onClick={dismiss}
          className="p-1.5 text-white/50 hover:text-white shrink-0"
          aria-label="dismiss"
          data-testid="pwa-install-dismiss-button"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
